import { useRef, useEffect, useState } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import fragmentShader from '../../shaders/video/fragment.glsl';
import vertexShader from '../../shaders/video/vertex.glsl';
import { videoCache } from '../../hooks/media/useMediaLoader';

interface MediaGalleryProps {
  media: string[];
}

export const MediaGallery: React.FC<MediaGalleryProps> = ({ media = [] }) => {
  const materialRefs = useRef<(THREE.ShaderMaterial | null)[]>([]);
  const textureRefs = useRef<(THREE.Texture | null)[]>([]);
  const { viewport } = useThree();
  const time = useRef(0);
  const [aspectRatios, setAspectRatios] = useState<number[]>([]);
  const isMobile = window.innerWidth < 768;

  const isVideo = (url: string) =>
    url.endsWith('.mp4') || url.endsWith('.webm');

  const loadImageAspectRatio = (url: string): Promise<number> => {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        resolve(img.width / img.height);
      };
      img.src = url;
    });
  };

  const createTexture = (source: HTMLVideoElement | string) => {
    if (source instanceof HTMLVideoElement) {
      const texture = new THREE.VideoTexture(source);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      return texture;
    } else {
      const texture = new THREE.TextureLoader().load(source);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      return texture;
    }
  };

  const getMaterial = (index: number, source: HTMLVideoElement | string) => {
    if (!materialRefs.current[index]) {
      const texture = createTexture(source);
      textureRefs.current[index] = texture;

      materialRefs.current[index] = new THREE.ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
          tDiffuse: { value: texture },
          mousePos: { value: new THREE.Vector2(0.5, 0.5) },
          hovered: { value: 0.0 },
          time: { value: 0.0 },
          transitionProgress: { value: 0.0 },
          scrollOffset: { value: 0.0 },
          resolution: { value: new THREE.Vector2(aspectRatios[index] || 1, 1) },
          distortionAmount: { value: 0.2 },
          distortionFrequency: { value: 3.0 },
          waveAmplitude: { value: 0.1 },
          waveFrequency: { value: 5.0 },
        },
        transparent: true,
      });
    }
    return materialRefs.current[index];
  };

  useEffect(() => {
    if (!media.length) return;

    materialRefs.current = media.map(() => null);
    textureRefs.current = media.map(() => null);

    const loadRatios = async () => {
      const ratios = await Promise.all(
        media.map(async url => {
          if (isVideo(url)) {
            const video = videoCache.get(url);
            return video ? video.videoWidth / video.videoHeight : 16 / 9;
          } else {
            return await loadImageAspectRatio(url);
          }
        })
      );
      setAspectRatios(ratios);
    };

    loadRatios();

    media.forEach(url => {
      if (isVideo(url)) {
        const video = videoCache.get(url);
        if (video && video.paused) {
          video.play().catch(console.error);
        }
      }
    });

    return () => {
      textureRefs.current.forEach(texture => {
        if (texture) {
          texture.dispose();
        }
      });
      materialRefs.current.forEach(material => {
        if (material) {
          material.dispose();
        }
      });
    };
  }, [media]);

  useEffect(() => {
    const animate = () => {
      time.current += 0.01;
      materialRefs.current.forEach(material => {
        if (material?.uniforms) {
          material.uniforms.time.value = time.current;
        }
      });
      requestAnimationFrame(animate);
    };

    const animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  if (!media?.length || aspectRatios.length !== media.length) return null;

  let currentX = 0;
  let currentY = 0;

  // Calcul des dimensions selon le mode
  if (isMobile) {
    // Sur mobile : empilé verticalement, largeur pleine
    currentY = viewport.height / 2; // Commencer en haut

    return (
      <group>
        {media.map((url, index) => {
          let source: HTMLVideoElement | string;
          if (isVideo(url)) {
            const video = videoCache.get(url);
            if (!video) return null;
            source = video;
          } else {
            source = url;
          }

          const material = getMaterial(index, source);
          if (!material) return null;

          const width = viewport.width;
          const height = width / aspectRatios[index];

          // Positionner verticalement et centrer horizontalement
          const yPosition = currentY - height / 2;
          currentY -=
            height +
            (index < media.length - 1 ? 50 * (1 / viewport.factor) : 0); // Gap de 20 unités en mobile

          return (
            <mesh
              key={url}
              position={[0, yPosition, 0]}
              onPointerEnter={() => {
                if (material.uniforms) {
                  material.uniforms.hovered.value = 1.0;
                }
              }}
              onPointerLeave={() => {
                if (material.uniforms) {
                  material.uniforms.hovered.value = 0.0;
                }
              }}
              onPointerMove={e => {
                if (material.uniforms) {
                  material.uniforms.mousePos.value.set(
                    e.uv?.x ?? 0.5,
                    e.uv?.y ?? 0.5
                  );
                }
              }}
            >
              <planeGeometry args={[width, height]} />
              <primitive object={material} attach="material" />
            </mesh>
          );
        })}
      </group>
    );
  } else {
    // Sur desktop : aligné horizontalement
    const totalWidth = media.reduce((acc, _, index) => {
      const height = viewport.height * 0.5;
      const width = height * aspectRatios[index];
      const spacing = index < media.length - 1 ? 70 * (1 / viewport.factor) : 0;
      return acc + width + spacing;
    }, 0);

    currentX = -totalWidth / 2;

    const getYOffset = (index: number) => {
      const imageHeight = viewport.height * 0.5;

      switch (index) {
        case 0: // Premier : bas aligné avec le bas du viewport
          return -viewport.height / 2 + imageHeight / 2;
        case 1: // Deuxième : haut aligné avec le haut du viewport
          return viewport.height / 2 - imageHeight / 2;
        case 2: // Troisième : commence à 70% de la hauteur depuis le bas
          return viewport.height * (0.7 - 0.5) - imageHeight / 2;
        case 3: // Quatrième : commence à 30% de la hauteur depuis le bas
          return viewport.height * 0.3 - imageHeight / 2;
        default:
          return 0;
      }
    };

    return (
      <group>
        {media.map((url, index) => {
          let source: HTMLVideoElement | string;
          if (isVideo(url)) {
            const video = videoCache.get(url);
            if (!video) return null;
            source = video;
          } else {
            source = url;
          }

          const material = getMaterial(index, source);
          if (!material) return null;

          const height = viewport.height * 0.5;
          const width = height * aspectRatios[index];

          const xPosition = currentX + width / 2;
          const yPosition = getYOffset(index);
          const spacingInUnits =
            index < media.length - 1 ? 70 * (1 / viewport.factor) : 0;

          currentX += width + spacingInUnits;

          return (
            <mesh
              key={url}
              position={[xPosition, yPosition, 0]}
              onPointerEnter={() => {
                if (material.uniforms) {
                  material.uniforms.hovered.value = 1.0;
                }
              }}
              onPointerLeave={() => {
                if (material.uniforms) {
                  material.uniforms.hovered.value = 0.0;
                }
              }}
              onPointerMove={e => {
                if (material.uniforms) {
                  material.uniforms.mousePos.value.set(
                    e.uv?.x ?? 0.5,
                    e.uv?.y ?? 0.5
                  );
                }
              }}
            >
              <planeGeometry args={[width, height]} />
              <primitive object={material} attach="material" />
            </mesh>
          );
        })}
      </group>
    );
  }
};
